import { ContratRef, LotTechnique, LotDepartement } from '../types';
import { estNumeroPanier, numeroPanierAffiche } from '../../../utils/numeroPanier';

// Recap read-only « Preparation du Bon de Commande ». Decalque du MS
// (SuiviOperationMSV2/domain/marche/preparation.ts) : memes champs, meme ordre, meme placeholder.
// Copie ISOLEE et non import cross-module : c'est la convention de ce projet pour les vues BC vs MS
// (cf. l'en-tete de OperationNotification.css, "aucun partage cross-module"). Les deux recaps
// divergeront d'ailleurs — celui-ci porte le rang, que le MS n'a pas.
//
// Derive UNIQUEMENT du payload de l'operation : aucun fetch dedie. Le titulaire vient de la commande
// du rang consulte, que core enrichit deja avec son contrat (numero + entite).

export interface RecapField {
  label: string;
  value: string;
}

const PLACEHOLDER = '—';

// Edition courante de l'accord-cadre, derivee de l'annee d'edition (env) plutot que figee en dur :
// l'onglet Consultation du BC porte encore "Accord-cadre edition 2025-2026" en clair, qui mentira au
// changement d'edition. Meme derivation que le MS.
const ANNEE = process.env.REACT_APP_ANNEE_EDITION || '25';
const ANNEE_DEBUT = `20${ANNEE}`;
const ANNEE_FIN = `20${Number(ANNEE) + 1}`;
export const ACCORD_CADRE_EDITION = `Accord-cadre édition ${ANNEE_DEBUT}-${ANNEE_FIN}`;
export const ACCORD_CADRE_VALIDITE = `01/01/${ANNEE_DEBUT} au 31/12/${ANNEE_FIN}`;

const lotLabel = (id?: number | string, nom?: string): string => {
  const parts = [id, nom].filter((p) => p !== undefined && p !== null && String(p).length > 0);
  return parts.length ? parts.join(' - ') : PLACEHOLDER;
};

const dateOnly = (iso?: string): string => {
  if (!iso) return PLACEHOLDER;
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? PLACEHOLDER
    : d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

// `commandeDuRang` vit dans domain/rangs (source unique des notions de rang) ; re-exporte ici pour les
// feuilles qui l'importaient avec le recap.
export { commandeDuRang } from './rangs';

// Forme MINIMALE attendue par le recap, et non `OperationBC` : les deux faces du BC n'ont pas le meme
// type d'operation (l'acheteur a `OperationBC`, le presta une `OperationRef` wrappee dans sa commande)
// alors qu'elles portent les MEMES champs ici. Typer sur ce dont on se sert evite un cast bidon a
// l'appel — c'est le contrat reel de cette fonction.
export interface PreparationOperation {
  numero?: string | number;
  nom?: string;
  date_fin_operation?: string;
  lot_technique?: LotTechnique;
  lot_departement?: LotDepartement;
}

/**
 * @param titulaire Commande du titulaire a decrire — celle du rang consulte cote acheteur, la sienne
 *   cote prestataire. C'est ce parametre, et non un rang, qui rend le recap utilisable des deux
 *   cotes : le presta n'a pas la liste des commandes de l'operation, il n'a que la sienne.
 */
export const buildPreparationRecap = (
  operation: PreparationOperation,
  titulaire?: { contrat?: ContratRef },
): RecapField[] => {
  const contrat = titulaire?.contrat;
  const entite = contrat?.entite;
  const dep = operation.lot_departement?.departement;

  // Le numero de bon de commande n'est attribue qu'a l'acceptation. Avant, `operation.numero` porte
  // encore le numero de PANIER : l'afficher sous le libelle « N° du Bon de Commande » annoncait un
  // numero contractuel qui n'existait pas encore. On nomme donc ce qu'on montre reellement, et le
  // panier s'affiche sans son prefixe (PBC / BC), comme dans l'encart des bannieres de suivi.
  const numeroEstPanier = estNumeroPanier(operation.numero);
  const champNumero: RecapField = numeroEstPanier
    ? { label: 'N° de panier', value: numeroPanierAffiche(operation.numero) || PLACEHOLDER }
    : { label: 'N° du Bon de Commande', value: String(operation.numero) };

  return [
    { label: 'Accord-cadre en vigueur', value: ACCORD_CADRE_EDITION },
    { label: 'Date de validité', value: ACCORD_CADRE_VALIDITE },
    { label: 'Lot technique', value: lotLabel(operation.lot_technique?.id, operation.lot_technique?.nom) },
    { label: 'Lot géographique', value: lotLabel(dep?.id, dep?.nom) },
    { label: 'N° contrat du titulaire', value: contrat?.numero || PLACEHOLDER },
    { label: 'Nom du titulaire', value: entite?.nom || PLACEHOLDER },
    { label: 'SIRET', value: entite?.siret || PLACEHOLDER },
    { label: "Date prévisionnelle de fin d'opération", value: dateOnly(operation.date_fin_operation) },
    { label: "Nom de l'opération", value: operation.nom || PLACEHOLDER },
    champNumero,
  ];
};
